// Backlog 109 layer A: applies lib/awayGuard.js to the signed-in agent's own
// session. See that file for the rules and why they exist.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useContact } from '../context/ContactContext';
import { apiHeaders } from '../lib/consoleApi';
import {
  AVAILABLE, OFFLINE, TICK_MS, HEARTBEAT_MS, idleStep, isBusy, secondsLeft,
  presenceUrl, shouldGoOfflineOnMiss,
} from '../lib/awayGuard';

const PRESENCE_URL = presenceUrl(import.meta.env.VITE_DATA_URL || '');

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];

export function useAwayGuard() {
  const c = useContact();
  const latest = useRef(c);
  latest.current = c;

  const [promptAt, setPromptAt] = useState(null);
  const [notice, setNotice] = useState(null);       // { reason: 'missed' | 'idle', at }
  const [now, setNow] = useState(() => Date.now());
  const promptRef = useRef(null);
  const lastActivity = useRef(Date.now());
  const missedSeen = useRef(null);

  const showPrompt = useCallback(at => { promptRef.current = at; setPromptAt(at); }, []);

  const goOffline = useCallback(reason => {
    showPrompt(null);
    setNotice({ reason, at: Date.now() });
    console.info(JSON.stringify({ msg: 'away-guard', action: 'offline', reason }));
    try { latest.current.handleSetAgentState(OFFLINE); } catch (err) { console.error('away-guard: could not go Offline', err); }
  }, [showPrompt]);

  // 1. A ring this console missed.
  useEffect(() => {
    if (c.isDemo || !c.callMissedAt || c.callMissedAt === missedSeen.current) return;
    missedSeen.current = c.callMissedAt;
    if (shouldGoOfflineOnMiss({ status: c.agentStatus })) goOffline('missed');
  }, [c.callMissedAt, c.agentStatus, c.isDemo, goOffline]);

  // 2. Idle while Available: prompt, then Offline.
  useEffect(() => {
    if (c.isDemo) return undefined;
    const mark = () => { lastActivity.current = Date.now(); };
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, mark, { passive: true }));
    const timer = setInterval(() => {
      const t = Date.now();
      setNow(t);
      const s = latest.current;
      const step = idleStep({
        status: s.agentStatus, busy: isBusy(s),
        lastActivityAt: lastActivity.current, promptAt: promptRef.current, now: t,
      });
      if (step === 'prompt') showPrompt(t);
      else if (step === 'cancel') showPrompt(null);
      else if (step === 'offline') goOffline('idle');
    }, TICK_MS);
    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, mark));
      clearInterval(timer);
    };
  }, [c.isDemo, goOffline, showPrompt]);

  // 3. Check in with the server every 30 s while signed in to the softphone,
  //    and tell it when the tab is closing. The server decides: it sets an
  //    Available agent who is not on a contact to Offline when their console
  //    goes quiet (closed tab, sleeping laptop, lost connection). The request
  //    on closing is allowed to outlive the page (keepalive); the headers are
  //    kept from the last check-in because nothing can be awaited by then.
  const headersRef = useRef(null);
  useEffect(() => {
    if (c.isDemo || !PRESENCE_URL || !c.agentConnected) return undefined;
    let stopped = false;
    const beat = async () => {
      try {
        const headers = await apiHeaders({ 'Content-Type': 'application/json' });
        if (stopped || !headers.Authorization) return;
        headersRef.current = headers;
        await fetch(PRESENCE_URL, { method: 'POST', headers, body: JSON.stringify({ type: 'heartbeat' }) });
      } catch { /* a missed check-in is harmless; three minutes of them is the signal */ }
    };
    beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    const onLeave = () => {
      if (!headersRef.current) return;
      try { fetch(PRESENCE_URL, { method: 'POST', headers: headersRef.current, body: JSON.stringify({ type: 'away' }), keepalive: true }); } catch { /* the page is going away */ }
    };
    window.addEventListener('pagehide', onLeave);
    return () => { stopped = true; clearInterval(timer); window.removeEventListener('pagehide', onLeave); };
  }, [c.isDemo, c.agentConnected]);

  // The notice goes once the agent is Available again.
  useEffect(() => { if (c.agentStatus === AVAILABLE) setNotice(null); }, [c.agentStatus]);

  const imHere = useCallback(() => { lastActivity.current = Date.now(); showPrompt(null); }, [showPrompt]);
  const goAvailable = useCallback(() => { lastActivity.current = Date.now(); latest.current.handleSetAgentState(AVAILABLE); }, []);
  const dismissNotice = useCallback(() => setNotice(null), []);

  return { promptAt, secondsLeft: secondsLeft(promptAt, now), notice, imHere, goAvailable, dismissNotice };
}
